import React from 'react';

import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { TextComponent } from 'design/text/textComponents';
import { MarginComponent } from 'design/margin';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { FieldsetLayoutComponent } from 'design/fieldsetLayout';
import { PartnerProfileFieldset } from 'containers/PartnerProfileFieldset';
import { usePartnerProfileForm } from 'hooks/usePartnerProfileForm';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { FormControlComponent } from 'design/formControl';
import { validatePartnerProfileFormValues } from 'utils/validatePartnerProfileFormValues';
import { useServices } from 'hooks/useServices';
import { PartnerManagerData } from 'types/partnerManager';
import { MaximumAllowedFileSizeExceededError } from 'utils/errors';

type Props = {
  partnerId: number;
  parnerManagerData: PartnerManagerData;
  onCancel(): void;
  onEdited(): void;
};

export const PartnerManagerProfileEditor = (props: Props) => {
  const { partnerId, parnerManagerData, onCancel: cancel, onEdited } = props;

  const {
    partnerManager: { profileEditorScreen: ownDictionary },
  } = useIntlDictionary();

  const { submitPartnerProfileChangeForm } = useServices();

  const { active: asyncActive, injectPromise } = useAsyncBoundary();

  const {
    partner: { profile: partnerProfile },
  } = parnerManagerData;

  const {
    values,
    violations,
    setValues,
    setViolations,
    setFirstName,
    setLastName,
    setMiddleName,
    setPhone,
    setCountry,
    setCity,
    setAvatar,
    setDetails,
  } = usePartnerProfileForm();

  React.useLayoutEffect(() => {
    setValues({
      ...partnerProfile,
      avatar: null,
    });
  }, [partnerProfile, setValues]);

  const submitForm = React.useCallback(() => {
    return submitPartnerProfileChangeForm(partnerId, values)
      .then(onEdited)
      .catch((error) => {
        if (error instanceof MaximumAllowedFileSizeExceededError) {
          setViolations((prev) => [...prev, { name: 'AvatarCroppedFileSize' }]);
        } else {
          throw error;
        }
      });
  }, [
    onEdited,
    partnerId,
    setViolations,
    submitPartnerProfileChangeForm,
    values,
  ]);

  const submit = React.useCallback(() => {
    if (violations.length === 0) {
      const nextViolations = validatePartnerProfileFormValues(values, true);

      if (nextViolations.length === 0) {
        const promise = submitForm();

        injectPromise(promise, { shouldShowFallback: false });
      } else {
        Promise.resolve(nextViolations).then(setViolations);
      }
    }
  }, [injectPromise, setViolations, submitForm, values, violations.length]);

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {ownDictionary.titleText}
          </TextComponent>
        }
        actions={null}
      />
      <FormControlComponent
        shouldFocusFirstInvalidInput={violations.length > 0}
        onSubmit={submit}
      >
        <SidebarModalSectionComponent
          content={
            <FieldsetLayoutComponent>
              <PartnerProfileFieldset
                disabled={asyncActive}
                prevProfile={partnerProfile}
                values={values}
                violations={violations}
                onFirstNameChange={setFirstName}
                onLastNameChange={setLastName}
                onMiddleNameChange={setMiddleName}
                onPhoneChange={setPhone}
                onCountryChange={setCountry}
                onCityChange={setCity}
                onAvatarChange={setAvatar}
                onDetailsChange={setDetails}
              />
            </FieldsetLayoutComponent>
          }
          actions={
            <>
              <ModalPressControlComponent theme="primary" type="submit">
                {ownDictionary.submitActionText}
              </ModalPressControlComponent>
              <MarginComponent top={20} />
              <ModalPressControlComponent
                theme="secondary"
                type="button"
                onPress={cancel}
              >
                {ownDictionary.cancelActionText}
              </ModalPressControlComponent>
            </>
          }
        />
      </FormControlComponent>
    </>
  );
};
